import { Link, useSearchParams } from 'react-router-dom'
import { Heart, Sparkles, Waves, Flame, Hand, Footprints, Package, ArrowRight, Gift, Sparkles as SparklesIcon, X, CheckCircle, Star, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react'
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Terms from './Terms'
import Privacy from './Privacy'
import { getAuth, signInAnonymously } from 'firebase/auth'
import { app, db } from '../firebase/config'
import { collection, getDocs, addDoc, query, orderBy, Timestamp } from 'firebase/firestore'
import toast from 'react-hot-toast'

const PAYSTACK_PUBLIC_KEY = 'pk_live_d0dac42be7424384d1b9c6dc660d3993efaf5cd6'

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showWellnessForm, setShowWellnessForm] = useState(false)
  const [showGreeting, setShowGreeting] = useState(false)
  const [legalModalType, setLegalModalType] = useState(null) // 'terms' | 'privacy' | null
  
  // Reviews state
  const [reviews, setReviews] = useState([])
  const [averageRating, setAverageRating] = useState(0)
  const [totalReviews, setTotalReviews] = useState(0)
  const [loadingReviews, setLoadingReviews] = useState(true)
  const [currentPage, setCurrentPage] = useState(0)
  
  // Review submission modal
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewForm, setReviewForm] = useState({
    name: '',
    rating: 0,
    comment: ''
  })
  const [submittingReview, setSubmittingReview] = useState(false)
  
  // Wellness Gift Card Form State
  const [wellnessForm, setWellnessForm] = useState({
    amount: '',
    customAmount: '',
    creatorName: '',
    creatorPhone: '',
    recipientName: '',
    recipientPhone: '',
    message: '',
    deliveryMethod: 'digital', // 'digital' or 'physical'
    address: '' // For physical delivery
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentReference, setPaymentReference] = useState(null)
  const [showCompleteButton, setShowCompleteButton] = useState(false)

  // Toggle from Firestore: whether to apply a small Paystack fee uplift
  const [applyPaystackFeeUplift, setApplyPaystackFeeUplift] = useState(true)

  useEffect(() => {
    const loadFeeFlag = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore')
        const settingsDocRef = doc(db, 'settings', 'booking')
        const snap = await getDoc(settingsDocRef)
        if (snap.exists()) {
          const data = snap.data()
          if (typeof data.applyPaystackFeeUplift === 'boolean') {
            setApplyPaystackFeeUplift(data.applyPaystackFeeUplift)
          }
        }
      } catch (e) {
        console.error('Failed to load payment fee settings:', e)
      }
    }
    loadFeeFlag()
  }, [])

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  // Fetch all reviews for display and average calculation
  const fetchReviews = async (reset = false) => {
    try {
      setLoadingReviews(true)
      const reviewsRef = collection(db, 'reviews')
      
      // Fetch all approved reviews
      const q = query(
        reviewsRef,
        orderBy('createdAt', 'desc')
      )
      
      const snapshot = await getDocs(q)
      const reviewsData = []
      
      snapshot.forEach((doc) => {
        const data = doc.data()
        // Only show approved reviews (or all if approved field doesn't exist for backward compatibility)
        if (data.approved !== false) {
          reviewsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt)
          })
        }
      })
      
      setReviews(reviewsData)
      
      // Calculate average rating
      const ratings = reviewsData
        .filter(r => r.rating)
        .map(r => Number(r.rating))
      
      if (ratings.length > 0) {
        const avg = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
        setAverageRating(avg)
        setTotalReviews(ratings.length)
      } else {
        setAverageRating(0)
        setTotalReviews(0)
      }
      
      setLoadingReviews(false)
    } catch (error) {
      console.error('Error fetching reviews:', error)
      setLoadingReviews(false)
      toast.error('Failed to load reviews')
    }
  }
  
  // Load reviews on component mount
  useEffect(() => {
    fetchReviews(true)
  }, [])
  
  // Check for review parameter in URL and auto-open review modal
  useEffect(() => {
    const reviewParam = searchParams.get('review')
    if (reviewParam === 'true' || reviewParam === '1') {
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        setShowReviewModal(true)
        // Scroll to reviews section
        setTimeout(() => {
          const reviewsSection = document.querySelector('[data-reviews-section]')
          if (reviewsSection) {
            reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 300)
        // Remove the parameter from URL without reloading
        setSearchParams({}, { replace: true })
      }, 500)
    }
  }, [searchParams, setSearchParams])
  
  // Handle pagination
  const handleNextPage = () => {
    const maxPage = Math.ceil(reviews.length / 3) - 1
    if (currentPage < maxPage) {
      setCurrentPage(currentPage + 1)
      // Scroll to top of reviews section
      setTimeout(() => {
        const reviewsSection = document.querySelector('[data-reviews-section]')
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }, 100)
    }
  }
  
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
      // Scroll to top of reviews section
      setTimeout(() => {
        const reviewsSection = document.querySelector('[data-reviews-section]')
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }, 100)
    }
  }
  
  // Submit review
  const handleSubmitReview = async (e) => {
    e.preventDefault()
    
    if (!reviewForm.name.trim() || reviewForm.rating === 0 || !reviewForm.comment.trim()) {
      toast.error('Please fill in all fields and select a rating')
      return
    }
    
    setSubmittingReview(true)
    
    try {
      // Authenticate anonymously if needed
      const auth = getAuth(app)
      let user = auth.currentUser
      if (!user) {
        await signInAnonymously(auth)
        user = auth.currentUser
      }
      
      // Save review to Firestore
      const reviewsRef = collection(db, 'reviews')
      await addDoc(reviewsRef, {
        name: reviewForm.name.trim(),
        rating: Number(reviewForm.rating),
        comment: reviewForm.comment.trim(),
        createdAt: Timestamp.now(),
        approved: true // Auto-approve for now, can be changed to require admin approval
      })
      
      toast.success('Thank you for your review!')
      setShowReviewModal(false)
      setReviewForm({ name: '', rating: 0, comment: '' })
      
      // Refresh reviews
      fetchReviews(true)
      setCurrentPage(0)
      
      // Scroll to reviews section to show the new review
      setTimeout(() => {
        const reviewsSection = document.querySelector('[data-reviews-section]')
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 500)
    } catch (error) {
      console.error('Error submitting review:', error)
      toast.error('Failed to submit review. Please try again.')
    } finally {
      setSubmittingReview(false)
    }
  }
  
  // Show greeting only on initial site load or refresh, not on navigation between pages
  useEffect(() => {
    // Check if this is a page refresh (using Performance Navigation Timing API)
    const navigationEntry = performance.getEntriesByType('navigation')[0]
    const isPageRefresh = navigationEntry && navigationEntry.type === 'reload'
    
    // If it's a refresh, clear the sessionStorage flag so greeting will show again
    if (isPageRefresh) {
      sessionStorage.removeItem('greetingShownInSession')
    }
    
    // Check if greeting was already shown during this navigation session
    const greetingShownInSession = sessionStorage.getItem('greetingShownInSession')
    
    // Show greeting if it hasn't been shown yet (either fresh load or after clearing on refresh)
    if (!greetingShownInSession) {
      const timer = setTimeout(() => {
        setShowGreeting(true)
        // Mark as shown in sessionStorage (this persists during navigation)
        sessionStorage.setItem('greetingShownInSession', 'true')
        
        // Auto-hide after 4 seconds
        setTimeout(() => {
          setShowGreeting(false)
        }, 4000)
      }, 1000) // Show after 1 second
      
      return () => clearTimeout(timer)
    }
    
    // If navigating from another page, sessionStorage has the flag, so greeting won't show
  }, [])

  // Function to reset form (accessible throughout component)
  const resetForm = () => {
    setWellnessForm({
      amount: '',
      customAmount: '',
      creatorName: '',
      creatorPhone: '',
      recipientName: '',
      recipientPhone: '',
      message: '',
      deliveryMethod: 'digital',
      address: ''
    })
  }

  const handleWellnessFormChange = (field, value) => {
    setWellnessForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleWellnessSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate form
      const amount = wellnessForm.amount === 'custom' ? wellnessForm.customAmount : wellnessForm.amount
      if (!wellnessForm.creatorName || !wellnessForm.creatorPhone) {
        toast.error('Please provide creator name and phone number', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        setIsSubmitting(false)
        return
      }
      if (!amount || parseFloat(amount) <= 0) {
        toast.error('Please select or enter a valid amount', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        setIsSubmitting(false)
        return
      }
      
      if (!wellnessForm.recipientName) {
        toast.error('Please provide recipient name', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        setIsSubmitting(false)
        return
      }

      // Validate based on delivery method
      if (wellnessForm.deliveryMethod === 'digital') {
        if (!wellnessForm.recipientPhone) {
          toast.error('Recipient phone is required for digital delivery', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSubmitting(false)
          return
        }
      } else if (wellnessForm.deliveryMethod === 'physical') {
        if (!wellnessForm.recipientPhone || !wellnessForm.address) {
          toast.error('Phone and Address are required for physical delivery', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSubmitting(false)
          return
        }
      }
      
      // Sign in anonymously to authenticate with Firebase Functions
      const auth = getAuth(app)
      let user = auth.currentUser
      
      if (!user) {
        try {
          const userCredential = await signInAnonymously(auth)
          user = userCredential.user
        } catch (authError) {
          toast.error('Failed to authenticate. Please try again.', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSubmitting(false)
          return
        }
      }
      
      // Use creator phone number as email for Paystack
      const phoneDigits = wellnessForm.creatorPhone.replace(/\D/g, '')
      const creatorEmail = (phoneDigits || 'customer') + '@temp.sparadiseluxe.com'
      
      // Calculate amount with optional Paystack fee uplift for payment
      const baseAmount = parseFloat(amount)
      const multiplier = applyPaystackFeeUplift ? 1.02 : 1
      const amountWithFee = baseAmount * multiplier
      
      const startPayment = () => {
        if (!window.PaystackPop) {
          toast.error('Payment system not available. Please check your connection and try again.', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSubmitting(false)
          return
        }
        
        const handler = window.PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: creatorEmail,
          amount: Math.round(amountWithFee * 100), // GHS → pesewas
          currency: 'GHS',
          ref: `WELLNESS_${Date.now()}`,
          metadata: {
            type: 'wellness',
            creatorName: wellnessForm.creatorName,
          },
          callback: function (response) {
            // Payment successful – reuse existing verification + completion flow
            setIsSubmitting(false)
            const ref = response.reference
            setPaymentReference(ref)
            handleCompletePurchase(ref)
          },
          onClose: function () {
            setIsSubmitting(false)
            toast.error('Payment was cancelled. Please try again.', {
              duration: 5000,
              style: {
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#ffffff',
                border: '2px solid #ffffff',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
              },
            })
          },
        })
        
        handler.openIframe()
      }
      
      // If Paystack script already loaded, start immediately
      if (window.PaystackPop) {
        startPayment()
        return
      }
      
      // Otherwise, load Paystack inline script (v1) then start
      const script = document.createElement('script')
      script.src = 'https://js.paystack.co/v1/inline.js'
      script.onload = startPayment
      script.onerror = () => {
        toast.error('Failed to load payment gateway. Please try again.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        setIsSubmitting(false)
      }
      document.body.appendChild(script)
    } catch (error) {
      toast.error('An error occurred. Please try again.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
      setIsSubmitting(false)
      resetForm()
    }
  }

  // Verification handler – can use override reference from Paystack callback
  const handleCompletePurchase = async (referenceOverride = null) => {
    const referenceToUse = referenceOverride || paymentReference
    if (!referenceToUse) {
      toast.error('Payment reference not found. Please try again.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
      return
    }

    setIsSubmitting(true)
    setShowCompleteButton(false)

    try {
      // Import Firebase Functions dynamically
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functionsInstance = getFunctions(app)
      
      // Show verification notice
      const verificationToast = toast.loading('Verifying payment status...', {
        style: {
          background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })

      // Wait a moment for Paystack to update transaction status
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Verify payment status with Paystack
      const verifyPayment = httpsCallable(functionsInstance, 'verifyGiftCardPayment')
      const verifyResult = await verifyPayment({
        reference: referenceToUse
      })

      const verifyData = verifyResult.data

      if (!verifyData) {
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        toast.error('Unable to verify payment status. Please check your payment or contact support.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        return
      }

      const status = verifyData.status

      // Check status according to Paystack documentation
      if (status === 'success') {
        // Payment successful! Complete the gift card creation
        toast.dismiss(verificationToast)
        
        // Calculate base amount (without fee)
        const amount = wellnessForm.amount === 'custom' ? wellnessForm.customAmount : wellnessForm.amount
        const baseAmount = parseFloat(amount)
        
        // Save gift card
        const saveGiftCard = httpsCallable(functionsInstance, 'saveGiftCard')
        const saveResult = await saveGiftCard({
          type: 'wellness',
          amount: baseAmount,
          creatorName: wellnessForm.creatorName,
          creatorPhone: wellnessForm.creatorPhone,
          recipientName: wellnessForm.recipientName,
          recipientPhone: wellnessForm.recipientPhone,
          message: wellnessForm.message || '',
          deliveryMethod: wellnessForm.deliveryMethod,
          address: wellnessForm.address || '',
          paymentReference: verifyData.reference,
          creatorEmail: wellnessForm.creatorPhone + '@temp.sparadiseluxe.com'
        })

        const saveData = saveResult.data

        if (!saveData || !saveData.success) {
          toast.error('Failed to save gift card. Please contact support.', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSubmitting(false)
          setShowCompleteButton(true)
          return
        }

        // Send SMS notifications (email is sent automatically via Firestore trigger)
        try {
          const completeFlow = httpsCallable(functionsInstance, 'completeGiftCardFlow')
          await completeFlow({
            giftCardId: saveData.giftCardId
          })
        } catch (smsError) {
          console.error('Error sending SMS notifications:', smsError)
          // Don't fail the whole flow if SMS fails - email should still be sent
        }

        toast.success('🎉 Gift card purchased successfully!', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
          iconTheme: {
            primary: '#ffffff',
            secondary: '#10b981',
          },
        })
        setShowWellnessForm(false)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
      } else if (status === 'abandoned') {
        // Transaction was abandoned
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically

        toast.error('Payment was abandoned. Please try again if you wish to complete the purchase.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      } else if (status === 'failed') {
        // Transaction failed
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically

        toast.error('Payment failed. Please try again or use a different payment method.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      } else if (status === 'pending' || status === 'processing' || status === 'ongoing' || status === 'queued') {
        // Transaction is still processing - wait a bit more and check again
        toast.loading('Payment is processing, please wait...', {
          id: verificationToast,
          style: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })

        setTimeout(async () => {
          try {
            const verifyResult2 = await verifyPayment({
              reference: paymentReference
            })
            const verifyData2 = verifyResult2.data

            if (verifyData2 && verifyData2.status === 'success') {
              toast.dismiss(verificationToast)
              // Recursively call handleCompletePurchase to complete the flow
              setPaymentReference(paymentReference)
              await handleCompletePurchase()
            } else {
              // Still pending or failed - treat as abandoned after waiting
              toast.dismiss(verificationToast)
              setIsSubmitting(false)
              setShowCompleteButton(false)
              setPaymentReference(null)
              resetForm()
              setShowWellnessForm(false) // Close form automatically
              toast.error('Payment status unclear. Please check your payment or contact support.', {
                duration: 5000,
                style: {
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: '600',
                },
              })
            }
          } catch (error) {
            console.error('Error re-verifying payment:', error)
            toast.dismiss(verificationToast)
            setIsSubmitting(false)
            setShowCompleteButton(false)
            setPaymentReference(null)
            resetForm()
            setShowWellnessForm(false) // Close form automatically
            toast.error('Unable to verify payment status. Please check your payment or contact support.', {
              duration: 5000,
              style: {
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                color: '#ffffff',
                border: '2px solid #ffffff',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
              },
            })
          }
        }, 3000) // Wait 3 more seconds for status to update
      } else if (status === 'reversed') {
        // Transaction was reversed/refunded
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        toast.error('Payment was reversed. Please contact support for assistance.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      } else {
        // Unknown status
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        toast.error('Payment status unclear. Please check your payment or contact support.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      }
    } catch (error) {
      console.error('Error verifying payment:', error)
      setIsSubmitting(false)
      setShowCompleteButton(false)
      setPaymentReference(null)
      resetForm()
      setShowWellnessForm(false) // Close form automatically
      toast.error('Unable to verify payment status. Please check your payment or contact support.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
    }
  }

  const services = [
    {
      category: 'Massage',
      icon: Heart,
      image: '/media/images/services/massage1.jpg',
      purpose: 'Relaxation, stress relief, body therapy',
      color: 'from-purple-400 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100',
      subtypes: [
        'Swedish Massage',
        'Deep Tissue Massage',
        'Aromatherapy Massage',
        'Hot Stone Massage',
        'Prenatal Massage',
        'Couple Massage',
        'Head & Neck Massage'
      ]
    },
    {
      category: 'Facials',
      icon: Sparkles,
      image: '/media/images/services/facials1.jpg',
      purpose: 'Skin rejuvenation, cleansing, hydration',
      color: 'from-pink-400 to-pink-600',
      bgColor: 'from-pink-50 to-pink-100',
      subtypes: [
        'Classic Deep Cleansing Facial',
        'Hydrating Facial',
        'Anti-Aging Facial',
        'Brightening Facial',
        'Acne Treatment Facial'
      ]
    },
    {
      category: 'Wax',
      icon: Flame,
      image: '/media/images/services/waxing1.png',
      purpose: 'Smooth, hair-free skin with care',
      color: 'from-amber-400 to-amber-600',
      bgColor: 'from-amber-50 to-amber-100',
      subtypes: [
        'Full Leg Wax',
        'Bikini Wax',
        'Underarm Wax',
        'Full Body Wax',
        'Brazilian Wax'
      ]
    },
    {
      category: 'Body Scrubbing',
      icon: Waves,
      image: '/media/images/services/bodyscrub1.png',
      purpose: 'Exfoliation, smoothness, glow',
      color: 'from-amber-400 to-amber-600',
      bgColor: 'from-amber-50 to-amber-100',
      subtypes: [
        'Coffee Scrub',
        'Salt Glow Scrub',
        'Sugar & Honey Scrub',
        'Shea Butter Polish',
        'Coconut Milk Scrub'
      ]
    },
    {
      category: 'Manicures',
      icon: Hand,
      image: '/media/images/services/manicure1.png',
      purpose: 'Nail care and hand pampering',
      color: 'from-rose-400 to-rose-600',
      bgColor: 'from-rose-50 to-rose-100',
      subtypes: [
        'Classic Manicure',
        'Gel Manicure',
        'French Manicure',
        'Spa Manicure',
        'Nail Art'
      ]
    },
    {
      category: 'Pedicures',
      icon: Footprints,
      image: '/media/images/services/pedicure1.png',
      purpose: 'Foot care and leg relaxation',
      color: 'from-pink-400 to-rose-500',
      bgColor: 'from-pink-50 to-rose-50',
      subtypes: [
        'Classic Pedicure',
        'Spa Pedicure',
        'Gel Pedicure',
        'French Pedicure',
        'Medical Pedicure'
      ]
    },
    {
      category: 'Combo Packages',
      icon: Package,
      images: [
        '/media/images/services/massage1.jpg',
        '/media/images/services/facials1.jpg',
        '/media/images/services/waxing1.png',
        '/media/images/services/bodyscrub1.png',
        '/media/images/services/manicure1.png',
        '/media/images/services/pedicure1.png'
      ],
      purpose: 'Combinations of our six services, tailored by you',
      color: 'from-purple-400 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100',
      subtypes: [
        'Massage & Facial',
        'Spa Day Package',
        'Couple Package',
        'Custom combos from admin'
      ]
    }
  ]

  // Rotating image index for Combo Packages card only
  const [comboImageIndex, setComboImageIndex] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => {
      setComboImageIndex(prev => (prev + 1) % 6)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="spa-section">
      {/* Simple Greeting Slide-in */}
      <div className={`fixed top-20 z-50 transition-all duration-500 ease-out ${
        showGreeting ? 'left-4' : '-left-96'
      }`}>
        <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-400 text-white px-4 py-3 rounded-lg shadow-lg">
          <p className="text-sm font-semibold">{getGreeting()}! Welcome to Spa~Radise Luxe</p>
        </div>
      </div>

      <div className="container-spa">
        <h1 className="text-4xl font-serif font-bold text-center mb-8">Welcome to Spa~Radise Luxe</h1>
        <p className="text-center text-xl text-gray-600 mb-12">
          Experience romantic spa rituals designed for deep relaxation and glow
        </p>

        {/* Services Summary Section – first */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-4 text-gray-800">
            Our Services
          </h2>
          <p className="text-center text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
            Discover our range of spa treatments designed to rejuvenate your body, mind, and spirit
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {services.map((service, serviceIndex) => {
              const IconComponent = service.icon
              return (
                <div
                  key={serviceIndex}
                  className={`card bg-gradient-to-br ${service.bgColor} border-2 border-white/50 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex`}
                >
                  <div className="flex flex-row flex-1 items-stretch min-h-0">
                    {/* Content Section - Left Side */}
                    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8">
                      {/* Category Header */}
                      <div className="mb-2 sm:mb-3 md:mb-4">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-serif font-bold text-gray-800 mb-0.5 sm:mb-1">
                          {service.category}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 leading-tight">{service.purpose}</p>
                      </div>

                      {/* Subtypes List */}
                      <div className="mt-3 sm:mt-4 md:mt-6">
                        <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3 uppercase tracking-wide">
                          Available Treatments:
                        </p>
                        <ul className="space-y-1 sm:space-y-1.5 md:space-y-2">
                          {service.subtypes.map((subtype, idx) => (
                            <li key={idx} className="flex items-center space-x-1.5 sm:space-x-2 text-gray-700">
                              <span className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-gradient-to-r ${service.color} flex-shrink-0`}></span>
                              <span className="text-xs sm:text-sm md:text-base leading-tight">{subtype}</span>
                            </li>
                          ))}
                          <li className="flex items-center space-x-1.5 sm:space-x-2 text-gray-700">
                            <span className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-gradient-to-r ${service.color} flex-shrink-0`}></span>
                            <span className="text-xs sm:text-sm md:text-base leading-tight font-semibold italic text-gray-600">and many more</span>
                          </li>
                        </ul>
                      </div>

                      {/* Book Service Button */}
                      <div className="mt-4 sm:mt-6">
                        <Link
                          to={`/services?category=${service.category.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`inline-flex items-center justify-center space-x-2 bg-gradient-to-r ${service.color} hover:opacity-90 text-white font-semibold py-2.5 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-sm sm:text-base`}
                        >
                          <span>Book {service.category} Service</span>
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>

                    {/* Image Section - Right Side */}
                    <div className="relative w-1/3 sm:w-2/5 md:w-1/3 flex-shrink-0 overflow-hidden bg-gray-200">
                      {service.images && service.images.length > 0 ? (
                        <div className="absolute inset-0 w-full h-full">
                          {service.images.map((img, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={img}
                              alt={`${service.category} ${imgIndex + 1}`}
                              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                                comboImageIndex === imgIndex ? 'opacity-100' : 'opacity-0'
                              }`}
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                          ))}
                        </div>
                      ) : service.image ? (
                        <img
                          src={service.image}
                          alt={service.category}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none' }}
                        />
                      ) : null}
                      <div className={`absolute top-2 right-2 sm:top-3 sm:right-3 bg-gradient-to-br ${service.color} rounded-lg sm:rounded-xl p-1.5 sm:p-2 shadow-lg opacity-90 z-10`}>
                        <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Services CTA */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-400 hover:from-purple-600 hover:via-pink-600 hover:to-rose-500 text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <span>Explore All Services</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Gift Card Section – romantic Spa~Radise styling */}
        <div className="mb-16">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-50/90 via-pink-50/80 to-gray-50/90 border border-purple-100 shadow-xl">
            {/* Soft decorative curve at top */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
            <div className="relative p-8 md:p-12">
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-300 via-pink-300 to-rose-200 shadow-lg mb-4">
                  <Gift className="w-8 h-8 text-white drop-shadow-sm" />
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-3">
                  Perfect Gifts for Your Loved Ones
                </h2>
                <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                  Give the gift of relaxation and wellness. Ideal for birthdays, anniversaries, or any special occasion.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Session Gift Card */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-purple-100 shadow-md hover:shadow-lg hover:border-pink-200 transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center shadow">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-serif font-bold text-gray-800">Session Gift Card</h3>
                      <p className="text-sm text-gray-500">For a specific spa treatment</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-5">
                    Gift a specific treatment: massage, facials, wax, body scrubs, manicures or pedicures.
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    <span>Browse Services</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Wellness Gift Card */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-pink-100 shadow-md hover:shadow-lg hover:border-rose-200 transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center shadow">
                      <SparklesIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-serif font-bold text-gray-800">Wellness Gift Card</h3>
                      <p className="text-sm text-gray-500">For any spa experience</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-5">
                    Let them choose: use for any service or multiple sessions.
                  </p>
                  <button
                    onClick={() => setShowWellnessForm(true)}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold py-3 px-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    <span>Create Gift Card</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="text-center">
                <Link
                  to="/gift-card"
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 font-semibold py-3 px-6 rounded-xl border-2 border-gray-200 hover:border-purple-200 hover:bg-purple-50/50 transition-all duration-300"
                >
                  <Gift className="w-5 h-5" />
                  <span>View All Gift Cards</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div className="mb-16" data-reviews-section>
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
              Don't just take our word for it - hear from our satisfied clients about their amazing spa experiences
            </p>
            
            {/* Average Rating Summary */}
            {totalReviews > 0 && (
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="flex items-center space-x-2">
                  <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                  <span className="text-3xl font-bold text-gray-800">{averageRating.toFixed(1)}</span>
                </div>
                <div className="text-gray-600">
                  <p className="text-sm">Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Reviews Container with Pagination */}
          <div className="relative">
            {loadingReviews ? (
              <div className="text-center py-12">
                <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No reviews yet. Be the first to share your experience!</p>
              </div>
            ) : (
              <>
                {/* Reviews Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {reviews.slice(currentPage * 3, (currentPage * 3) + 3).map((review) => {
                    const initials = review.name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)
                    
                    const gradientColors = [
                      'from-purple-500 to-pink-500',
                      'from-pink-400 to-amber-500',
                      'from-blue-400 to-purple-500',
                      'from-amber-400 to-orange-500',
                      'from-purple-400 to-pink-500'
                    ]
                    const colorIndex = review.name.charCodeAt(0) % gradientColors.length
                    
                    return (
                      <div
                        key={review.id}
                        className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                      >
                        <div className="flex items-center mb-4">
                          <div className={`w-12 h-12 bg-gradient-to-br ${gradientColors[colorIndex]} rounded-full flex items-center justify-center text-white font-bold text-lg mr-4`}>
                            {initials}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">{review.name}</h4>
                            <div className="flex text-yellow-400 text-sm">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400' : 'fill-gray-300'}`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-700 italic leading-relaxed">
                          "{review.comment}"
                        </p>
                        {review.createdAt && (
                          <p className="text-xs text-gray-500 mt-3">
                            {review.createdAt.toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
                
                {/* Pagination Controls with Page Indicator */}
                {reviews.length > 3 && (
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 0}
                      className="flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border-2 border-purple-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg"
                      aria-label="Previous reviews"
                    >
                      <ChevronLeft className="w-6 h-6 text-purple-600" />
                    </button>
                    
                    <div className="text-sm text-gray-600 font-medium">
                      Page {currentPage + 1} of {Math.ceil(reviews.length / 3)}
                    </div>
                    
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= Math.ceil(reviews.length / 3) - 1}
                      className="flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border-2 border-purple-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg"
                      aria-label="Next reviews"
                    >
                      <ChevronRight className="w-6 h-6 text-purple-600" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Add Review Button */}
          <div className="text-center mt-8">
            <button
              onClick={() => setShowReviewModal(true)}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <MessageSquare className="w-5 h-5" />
              <span>Share Your Experience</span>
            </button>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl border border-purple-100 py-10 px-4">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-gray-800">Ready to Book Your Spa~Radise Luxe Experience?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Choose your perfect treatment and let us bring the spa magic straight to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services" className="btn-primary">View Our Services</Link>
            <Link to="/contact" className="btn-romantic">Contact Us</Link>
          </div>
        </div>

        {/* Gallery preview strip – 6 images */}
        <div className="mt-16">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-center mb-3 text-gray-800">
            A glimpse of Spa~Radise moments
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            A few scenes from candles, towels and soft-night setups. See more in our full gallery.
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3 max-w-5xl mx-auto">
            {[
              '/media/images/gallery/about1.jpeg',
              '/media/images/gallery/about2.jpeg',
              '/media/images/gallery/about3.jpeg',
              '/media/images/gallery/about4.jpeg',
              '/media/images/gallery/about5.jpeg',
              '/media/images/services/massage1.jpg',
            ].map((src, idx) => (
              <button
                key={src}
                type="button"
                onClick={() => window.location.assign('/gallery')}
                className="group relative block rounded-2xl overflow-hidden bg-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                aria-label="Open gallery"
              >
                <img
                  src={src}
                  alt={`Spa~Radise gallery preview ${idx + 1}`}
                  className="w-full h-24 sm:h-28 md:h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors" />
              </button>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/gallery"
              className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900"
            >
              <span>View full gallery</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Social Media section (mirrors Contact page buttons) */}
        <div className="mt-16 mb-20 max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-800">
              Stay connected with Spa~Radise Luxe
            </h2>
            <p className="text-gray-600 mt-2">
              Follow us on social media to see more moments, offers, and behind-the-scenes bliss.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <a 
              href="https://www.instagram.com/spa_radise579?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 bg-pink-600 hover:bg-pink-700 text-white p-3 rounded-lg transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781c-.49 0-.98-.49-.98-.98s.49-.98.98-.98.98.49.98.98-.49.98-.98.98z"/>
              </svg>
              <span className="text-sm">Instagram</span>
            </a>
            
            <a 
              href="https://wa.me/233537813579" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              <span className="text-sm">WhatsApp</span>
            </a>
            
            <a 
              href="https://vm.tiktok.com/ZS9eGpJ16woLp-jVb5G/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 bg-black hover:bg-gray-800 text-white p-3 rounded-lg transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              <span className="text-sm">TikTok</span>
            </a>
            
            <a 
              href="https://www.facebook.com/profile.php?id=61588273271316" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 12.07C22 6.48 17.52 2 11.93 2 6.35 2 1.87 6.48 1.87 12.07c0 4.78 3.44 8.74 7.94 9.64v-6.82H7.43v-2.82h2.38V9.41c0-2.35 1.4-3.65 3.55-3.65 1.03 0 2.1.18 2.1.18v2.31h-1.18c-1.16 0-1.52.72-1.52 1.46v1.76h2.59l-.41 2.82h-2.18v6.82c4.5-.9 7.94-4.86 7.94-9.64z" />
              </svg>
              <span className="text-sm">Facebook</span>
            </a>
          </div>
        </div>

        {/* Review Submission Modal */}
        {showReviewModal && createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative z-[10000]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <MessageSquare className="w-6 h-6" />
                  <h2 className="text-2xl font-serif font-bold">Share Your Experience</h2>
                </div>
                <button
                  onClick={() => {
                    setShowReviewModal(false)
                    setReviewForm({ name: '', rating: 0, comment: '' })
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                  disabled={submittingReview}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmitReview} className="p-6 space-y-6">
                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={reviewForm.name}
                    onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                    required
                    disabled={submittingReview}
                  />
                </div>

                {/* Rating Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Rating <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setReviewForm({ ...reviewForm, rating })}
                        className={`transition-all transform hover:scale-110 ${
                          reviewForm.rating >= rating
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                        disabled={submittingReview}
                      >
                        <Star
                          className={`w-8 h-8 ${
                            reviewForm.rating >= rating
                              ? 'fill-yellow-400'
                              : 'fill-none'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  {reviewForm.rating > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {reviewForm.rating === 5 && 'Excellent!'}
                      {reviewForm.rating === 4 && 'Great!'}
                      {reviewForm.rating === 3 && 'Good'}
                      {reviewForm.rating === 2 && 'Fair'}
                      {reviewForm.rating === 1 && 'Poor'}
                    </p>
                  )}
                </div>

                {/* Comment Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Review <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Share your Spa~Radise Luxe experience..."
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all resize-none"
                    required
                    disabled={submittingReview}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {reviewForm.comment.length} characters
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowReviewModal(false)
                      setReviewForm({ name: '', rating: 0, comment: '' })
                    }}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submittingReview}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingReview || reviewForm.rating === 0}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                  >
                    {submittingReview ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        <span>Submit Review</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Wellness Gift Card Form Modal - Rendered via Portal */}
        {showWellnessForm && createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" style={{ zIndex: 100000 }}>
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-amber-400 to-pink-400 text-white p-6 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <SparklesIcon className="w-6 h-6" />
                  <h2 className="text-2xl font-serif font-bold">Spa~Radise Luxe Wellness Gift Card</h2>
                </div>
                <button
                  onClick={() => {
                    setShowWellnessForm(false)
                    setShowCompleteButton(false)
                    setPaymentReference(null)
                    if (!isSubmitting) {
                      resetForm()
                    }
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Form Content */}
              <form onSubmit={handleWellnessSubmit} className="p-6 md:p-8 space-y-6">
                {/* Amount Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Gift Card Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                    {['200', '300', '500', '750', '1000'].map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => handleWellnessFormChange('amount', amount)}
                        className={`py-3 px-4 rounded-lg border-2 transition-all ${
                          wellnessForm.amount === amount
                            ? 'border-green-500 bg-green-50 text-green-700 font-semibold'
                            : 'border-gray-300 hover:border-green-300 text-gray-700'
                        }`}
                      >
                        GHS {amount}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleWellnessFormChange('amount', 'custom')}
                      className={`py-3 px-4 rounded-lg border-2 transition-all ${
                        wellnessForm.amount === 'custom'
                          ? 'border-green-500 bg-green-50 text-green-700 font-semibold'
                          : 'border-gray-300 hover:border-green-300 text-gray-700'
                      }`}
                    >
                      Custom
                    </button>
                  </div>
                  {wellnessForm.amount === 'custom' && (
                    <input
                      type="number"
                      min="50"
                      step="10"
                      value={wellnessForm.customAmount}
                      onChange={(e) => handleWellnessFormChange('customAmount', e.target.value)}
                      placeholder="Enter custom amount (min. GHS 50)"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    />
                  )}
                </div>

                {/* Creator Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Creator Name <span className="text-red-500">*</span></label>
                    <input type="text" required value={wellnessForm.creatorName} onChange={(e)=>handleWellnessFormChange('creatorName', e.target.value)} placeholder="Your full name" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Creator Phone <span className="text-red-500">*</span></label>
                    <input type="tel" required value={wellnessForm.creatorPhone} onChange={(e)=>handleWellnessFormChange('creatorPhone', e.target.value)} placeholder="024 XXX XXXX" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" />
                  </div>
                </div>

                {/* Recipient Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                    <Heart className="w-5 h-5 text-pink-500" />
                    <span>Recipient Details</span>
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={wellnessForm.recipientName}
                      onChange={(e) => handleWellnessFormChange('recipientName', e.target.value)}
                      placeholder="Enter recipient's full name"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Phone Number
                    </label>
                    <input
                      type="tel"
                      value={wellnessForm.recipientPhone}
                      onChange={(e) => handleWellnessFormChange('recipientPhone', e.target.value)}
                      placeholder="+233 XX XXX XXXX"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    />
                  </div>
                </div>

                {/* Optional Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Message (Optional)
                  </label>
                  <textarea
                    value={wellnessForm.message}
                    onChange={(e) => handleWellnessFormChange('message', e.target.value)}
                    placeholder="Add a personal message for the recipient..."
                    rows={4}
                    maxLength={200}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {wellnessForm.message.length}/200 characters
                  </p>
                </div>

                {/* Delivery Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Delivery Method
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-400 transition-all">
                      <input
                        type="radio"
                        name="deliveryMethod"
                        value="digital"
                        checked={wellnessForm.deliveryMethod === 'digital'}
                        onChange={(e) => handleWellnessFormChange('deliveryMethod', e.target.value)}
                        className="w-4 h-4 text-green-500"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">Digital Delivery</div>
                        <div className="text-sm text-gray-600">Sent instantly via SMS or WhatsApp</div>
                      </div>
                    </label>
                    <label className="flex items-center space-x-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-400 transition-all">
                      <input
                        type="radio"
                        name="deliveryMethod"
                        value="physical"
                        checked={wellnessForm.deliveryMethod === 'physical'}
                        onChange={(e) => handleWellnessFormChange('deliveryMethod', e.target.value)}
                        className="w-4 h-4 text-green-500"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">Physical Card</div>
                        <div className="text-sm text-gray-600">Delivered within 24-48 hours (delivery fee applies)</div>
                      </div>
                    </label>
                  </div>
                  
                  {/* Address field for physical delivery */}
                  {wellnessForm.deliveryMethod === 'physical' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Delivery Address <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        required={wellnessForm.deliveryMethod === 'physical'}
                        value={wellnessForm.address}
                        onChange={(e) => handleWellnessFormChange('address', e.target.value)}
                        placeholder="Enter delivery address"
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all resize-none"
                      />
                    </div>
                  )}
                </div>

                {/* Terms Notice */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    By proceeding, you agree to our{' '}
                    <button
                      type="button"
                      onClick={() => setLegalModalType('terms')}
                      className="text-blue-900 underline font-semibold hover:text-blue-950"
                    >
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      onClick={() => setLegalModalType('privacy')}
                      className="text-blue-900 underline font-semibold hover:text-blue-950"
                    >
                      Privacy Policy
                    </button>.
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowWellnessForm(false)
                      setShowCompleteButton(false)
                      setPaymentReference(null)
                      if (!isSubmitting) {
                        resetForm()
                      }
                    }}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <Gift className="w-5 h-5" />
                        <span>Proceed to Payment</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Legal modal for Terms / Privacy (non-blocking) */}
      {legalModalType && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-800">
                {legalModalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </h2>
              <button
                type="button"
                onClick={() => setLegalModalType(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="p-4">
                {legalModalType === 'terms' ? <Terms /> : <Privacy />}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setLegalModalType(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Home